// ═══════════════════════════════════════════════════════════════════════════
// Campaign Data API Source Templates
// ═══════════════════════════════════════════════════════════════════════════
//
// Add these to API_SOURCE_TEMPLATES in APISourcesTab.js.
// They follow the same shape as the existing templates (crm_sales,
// transactions, event_stream) but with campaign-specific data_intent.
//
// Each template includes:
//   - data_intent: campaign_response or campaign_conversion
//   - column_schema: the expected webhook payload structure from the vendor
//   - column_roles: pre-mapped to customer_key, event_type, event_timestamp,
//     event_value. User can override in the mapping editor.
//   - merge_mode: auto for real-time feeds
//   - rate_limit_per_minute: sized to typical vendor throughput
//
// After integration, these appear in the APISourcesTab template picker,
// filterable by category='campaign_channels' and 'campaign_attribution'.

const CAMPAIGN_API_SOURCE_TEMPLATES = {

  // ── Campaign Channels (response events: delivered, opened, clicked) ──
  campaign_channels: {
    category: 'Campaign Channels',
    icon: '📢',
    templates: [

      {
        id: 'sendgrid_events',
        name: 'SendGrid – Event Webhook',
        description: 'Real-time email engagement events (delivered, opened, clicked, bounced, unsubscribed)',
        provider: 'SendGrid',
        logo: '✉️',
        data_intent: 'campaign_response',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 5000,
        column_schema: [
          { name: 'email', data_type: 'string', required: true },
          { name: 'timestamp', data_type: 'datetime', required: true },
          { name: 'event', data_type: 'string', required: true },
          { name: 'sg_event_id', data_type: 'string', required: true },
          { name: 'sg_message_id', data_type: 'string', required: true },
          { name: 'category', data_type: 'string', required: false },
          { name: 'url', data_type: 'string', required: false },
          { name: 'reason', data_type: 'string', required: false },
        ],
        primary_key_columns: ['sg_event_id'],
        column_roles: {
          customer_key: 'email',
          event_type: 'event',
          event_timestamp: 'timestamp',
          campaign_id: 'category',
        },
        docs_link: 'https://docs.sendgrid.com/for-developers/tracking-events/event',
      },

      {
        id: 'mailchimp_webhook',
        name: 'Mailchimp – Webhooks',
        description: 'Campaign activity events (sent, opened, clicked, unsubscribed, cleaned)',
        provider: 'Mailchimp',
        logo: '🐵',
        data_intent: 'campaign_response',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 2000,
        column_schema: [
          { name: 'type', data_type: 'string', required: true },
          { name: 'fired_at', data_type: 'datetime', required: true },
          { name: 'data_email', data_type: 'string', required: true },
          { name: 'data_campaign_id', data_type: 'string', required: true },
          { name: 'data_list_id', data_type: 'string', required: false },
          { name: 'data_ip', data_type: 'string', required: false },
        ],
        primary_key_columns: ['type', 'fired_at', 'data_email'],
        column_roles: {
          customer_key: 'data_email',
          event_type: 'type',
          event_timestamp: 'fired_at',
          campaign_id: 'data_campaign_id',
        },
        docs_link: 'https://mailchimp.com/developer/marketing/guides/sync-audience-data-webhooks/',
      },

      {
        id: 'interakt_whatsapp',
        name: 'Interakt – WhatsApp Webhook',
        description: 'WhatsApp Business message events: delivery, read, button clicks, replies',
        provider: 'Interakt',
        logo: '💬',
        data_intent: 'campaign_response',
        merge_mode: 'auto',
        merge_interval_seconds: 30,
        rate_limit_per_minute: 10000,
        column_schema: [
          { name: 'customer_number', data_type: 'string', required: true },
          { name: 'event_type', data_type: 'string', required: true },
          { name: 'event_timestamp', data_type: 'datetime', required: true },
          { name: 'message_id', data_type: 'string', required: true },
          { name: 'template_name', data_type: 'string', required: false },
          { name: 'button_payload', data_type: 'string', required: false },
          { name: 'reply_text', data_type: 'string', required: false },
          { name: 'campaign_reference', data_type: 'string', required: false },
        ],
        primary_key_columns: ['message_id', 'event_type'],
        column_roles: {
          customer_key: 'customer_number',
          event_type: 'event_type',
          event_timestamp: 'event_timestamp',
          campaign_id: 'campaign_reference',
        },
        docs_link: 'https://www.interakt.shop/resource-center/webhooks-for-whatsapp-business-events/',
      },

      {
        id: 'twilio_sms_status',
        name: 'Twilio – SMS Status Callback',
        description: 'SMS delivery status updates (queued, sent, delivered, failed, undelivered)',
        provider: 'Twilio',
        logo: '📱',
        data_intent: 'campaign_response',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 3000,
        column_schema: [
          { name: 'To', data_type: 'string', required: true },
          { name: 'From', data_type: 'string', required: true },
          { name: 'MessageStatus', data_type: 'string', required: true },
          { name: 'MessageSid', data_type: 'string', required: true },
          { name: 'AccountSid', data_type: 'string', required: false },
          { name: 'ErrorCode', data_type: 'string', required: false },
        ],
        primary_key_columns: ['MessageSid', 'MessageStatus'],
        column_roles: {
          customer_key: 'To',
          event_type: 'MessageStatus',
        },
        notes: 'Twilio status callbacks do not include a native timestamp. The ingestion layer will stamp event_timestamp at receive time.',
        docs_link: 'https://www.twilio.com/docs/sms/send-messages#monitor-the-status-of-your-message',
      },

      {
        id: 'jio_rcs_delivery',
        name: 'Jio RCS – Delivery Report',
        description: 'RCS message events (delivered, read, button_clicked, media_opened)',
        provider: 'Jio',
        logo: '📡',
        data_intent: 'campaign_response',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 5000,
        column_schema: [
          { name: 'msisdn', data_type: 'string', required: true },
          { name: 'event', data_type: 'string', required: true },
          { name: 'eventTime', data_type: 'datetime', required: true },
          { name: 'messageId', data_type: 'string', required: true },
          { name: 'campaignId', data_type: 'string', required: false },
          { name: 'buttonLabel', data_type: 'string', required: false },
          { name: 'suggestionType', data_type: 'string', required: false },
        ],
        primary_key_columns: ['messageId', 'event'],
        column_roles: {
          customer_key: 'msisdn',
          event_type: 'event',
          event_timestamp: 'eventTime',
          campaign_id: 'campaignId',
        },
        docs_link: 'https://developers.google.com/business-communications/rcs-business-messaging/reference/rest',
      },

    ],
  },

  // ── Campaign Attribution (conversion events: purchase, signup, redemption) ──
  campaign_attribution: {
    category: 'Campaign Attribution',
    icon: '💰',
    templates: [

      {
        id: 'razorpay_orders',
        name: 'Razorpay – Order Paid Webhook',
        description: 'Captured payments with campaign attribution via order metadata',
        provider: 'Razorpay',
        logo: '🇮🇳',
        data_intent: 'campaign_conversion',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 1000,
        column_schema: [
          { name: 'event', data_type: 'string', required: true },
          { name: 'created_at', data_type: 'datetime', required: true },
          { name: 'payload_payment_entity_id', data_type: 'string', required: true },
          { name: 'payload_payment_entity_email', data_type: 'string', required: false },
          { name: 'payload_payment_entity_contact', data_type: 'string', required: false },
          { name: 'payload_payment_entity_amount', data_type: 'integer', required: true },
          { name: 'payload_payment_entity_currency', data_type: 'string', required: false },
          { name: 'payload_payment_entity_order_id', data_type: 'string', required: true },
          { name: 'payload_payment_entity_notes_campaign_id', data_type: 'string', required: false },
        ],
        primary_key_columns: ['payload_payment_entity_id'],
        column_roles: {
          customer_key: 'payload_payment_entity_contact',
          event_type: 'event',
          event_timestamp: 'created_at',
          event_value: 'payload_payment_entity_amount',
          campaign_id: 'payload_payment_entity_notes_campaign_id',
        },
        notes: 'Razorpay amounts are in paise (1 INR = 100 paise). Apply / 100 transformation on event_value.',
        docs_link: 'https://razorpay.com/docs/webhooks/payloads/payments/',
      },

      {
        id: 'generic_conversion_webhook',
        name: 'Generic – Conversion Webhook',
        description: 'Vendor-agnostic conversion/purchase events with explicit campaign link',
        provider: 'Generic',
        logo: '🔗',
        data_intent: 'campaign_conversion',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 2000,
        column_schema: [
          { name: 'customer_id', data_type: 'string', required: true },
          { name: 'event_type', data_type: 'string', required: true },
          { name: 'event_timestamp', data_type: 'datetime', required: true },
          { name: 'event_value', data_type: 'decimal', required: false },
          { name: 'event_currency', data_type: 'string', required: false },
          { name: 'campaign_id', data_type: 'string', required: false },
          { name: 'source_channel', data_type: 'string', required: false },
          { name: 'external_id', data_type: 'string', required: false },
        ],
        primary_key_columns: ['external_id'],
        column_roles: {
          customer_key: 'customer_id',
          event_type: 'event_type',
          event_timestamp: 'event_timestamp',
          event_value: 'event_value',
          campaign_id: 'campaign_id',
        },
      },

    ],
  },

};

// Helper: flatten into a single list with category metadata, filtered to
// campaign-related intents only. Use this when presenting templates from
// within the Campaigns tab.
export const getCampaignTemplates = () => {
  const out = [];
  for (const [catId, cat] of Object.entries(CAMPAIGN_API_SOURCE_TEMPLATES)) {
    cat.templates.forEach(t => {
      out.push({
        ...t,
        category: cat.category,
        category_id: catId,
        category_icon: cat.icon,
      });
    });
  }
  return out;
};

// Helper: check if a template is campaign-related by intent
export const isCampaignTemplate = (template) =>
  ['campaign_response', 'campaign_conversion'].includes(template?.data_intent);

export default CAMPAIGN_API_SOURCE_TEMPLATES;
